"use client";

import React, { useEffect, useState } from "react";
import { getHttpClientConfig } from "@/config/http-client";
import { Text, Price } from "@pamperedchef/design-system";

import * as styles from "./CartSubtotal.module.scss";

export interface CartSubtotalProps {
	locale?: string;
	currency?: string;
	/** When set, skips the checkout API fetch and renders this amount. */
	amount?: number;
}

interface CheckoutCartResponse {
	status?: string;
	result?: {
		subtotal?: number;
		cartSubtotal?: number;
		orderSummary?: {
			subtotal?: number;
		};
		entries?: Array<{ totalPrice?: number }>;
	};
}

function readSubtotal(data: CheckoutCartResponse | null): number | null {
	if (!data?.result) return null;

	const { subtotal, cartSubtotal, orderSummary, entries } = data.result;

	if (typeof subtotal === "number") return subtotal;
	if (typeof cartSubtotal === "number") return cartSubtotal;
	if (typeof orderSummary?.subtotal === "number") {
		return orderSummary.subtotal;
	}

	if (Array.isArray(entries) && entries.length > 0) {
		const sum = entries.reduce(
			(total, entry) => total + (entry.totalPrice ?? 0),
			0
		);
		return sum > 0 ? sum : null;
	}

	return null;
}

export function CartSubtotal({
	locale = "en-US",
	currency = "USD",
	amount
}: CartSubtotalProps): JSX.Element | null {
	const [subtotal, setSubtotal] = useState<number | null>(amount ?? null);

	useEffect(() => {
		if (amount !== undefined) {
			setSubtotal(amount);
			return;
		}

		let cancelled = false;

		fetch("/consumer-cart-checkout-api/v2/cart", {
			credentials: "include",
			signal: AbortSignal.timeout(
				getHttpClientConfig().timeoutSeconds * 1000
			)
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((data: CheckoutCartResponse | null) => {
				if (cancelled) return;
				setSubtotal(readSubtotal(data));
			})
			.catch(() => {
				if (!cancelled) setSubtotal(null);
			});

		return () => {
			cancelled = true;
		};
	}, [amount]);

	if (subtotal === null) return null;

	return (
		<div className={styles.row} data-component="CartSubtotal">
			<Text tag="span" styledAs="smb" className={styles.label}>
				Subtotal
			</Text>
			<Price
				display={subtotal}
				locale={locale}
				currency={currency}
				size="small"
				className={styles.amount}
			/>
		</div>
	);
}
